const API_BASE = process.env.REACT_APP_BACKEND_URL;
const FAILURE_THRESHOLD = 3;
const CHECK_TIMEOUT_MS = 10000;
const STABLE_INTERVAL_MS = 30000;
const BACKOFF_MS = [1000, 2000, 5000, 10000, 15000];

class ConnectivityManager {
  constructor() {
    this.state = { online: true, checking: false, lastSuccess: null, failures: 0, retryCount: 0 };
    this._subs = new Set();
    this._timer = null;
    this._started = false;
    this._inFlight = null;
    this._lastFailureReport = 0;
    this._onBrowserOnline = () => {
      console.info("[NETWORK] Browser online event");
      this.checkNow("browser-online");
    };
    this._onBrowserOffline = () => {
      console.info("[NETWORK] Browser offline event");
      this.checkNow("browser-offline");
    };
    // iOS Safari freezes timers while backgrounded/locked — re-verify immediately on resume
    this._onVisible = () => {
      if (document.visibilityState === "visible") this.checkNow("app-resumed");
    };
    this._onPageShow = () => this.checkNow("pageshow");
  }

  start() {
    if (this._started) return;
    this._started = true;
    window.addEventListener("online", this._onBrowserOnline);
    window.addEventListener("offline", this._onBrowserOffline);
    document.addEventListener("visibilitychange", this._onVisible);
    window.addEventListener("pageshow", this._onPageShow);
    this.checkNow("startup");
  }

  stop() {
    if (!this._started) return;
    this._started = false;
    window.removeEventListener("online", this._onBrowserOnline);
    window.removeEventListener("offline", this._onBrowserOffline);
    document.removeEventListener("visibilitychange", this._onVisible);
    window.removeEventListener("pageshow", this._onPageShow);
    clearTimeout(this._timer);
  }

  subscribe(cb) {
    this._subs.add(cb);
    return () => this._subs.delete(cb);
  }

  _emit() {
    const snapshot = { ...this.state };
    this._subs.forEach((cb) => { try { cb(snapshot); } catch { /* */ } });
  }

  _logEvent(type, detail = "") {
    if (!this.log) this.log = [];
    this.log.push({ at: Date.now(), type, detail });
    if (this.log.length > 100) this.log.shift();
  }

  getLog() { return [...(this.log || [])].reverse(); }

  _schedule() {
    clearTimeout(this._timer);
    if (!this._started) return;
    let delay;
    if (this.state.online && this.state.failures === 0) {
      delay = STABLE_INTERVAL_MS;
    } else {
      delay = BACKOFF_MS[Math.min(this.state.retryCount, BACKOFF_MS.length - 1)];
      this.state.retryCount += 1;
    }
    this._timer = setTimeout(() => this.checkNow("scheduled"), delay);
  }

  async checkNow(reason = "manual") {
    if (this._inFlight) return this._inFlight;
    this._inFlight = this._check(reason).finally(() => { this._inFlight = null; });
    return this._inFlight;
  }

  async _probe() {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), CHECK_TIMEOUT_MS);
    try {
      const res = await fetch(`${API_BASE}/api/health?t=${Date.now()}`, { cache: "no-store", signal: ctrl.signal });
      return { ok: res.ok, detail: res.ok ? "" : `HTTP ${res.status}` };
    } catch (e) {
      return { ok: false, detail: e.name === "AbortError" ? "timeout" : "network error" };
    } finally {
      clearTimeout(timer);
    }
  }

  async _check(reason) {
    this.state.checking = true;
    this._emit();
    // Double-probe: a single dropped packet / transient reset never counts as a failure
    let { ok, detail: failDetail } = await this._probe();
    if (!ok) {
      await new Promise((r) => setTimeout(r, 500));
      const second = await this._probe();
      ok = second.ok;
      if (!ok) failDetail = second.detail || failDetail;
      else console.info("[NETWORK] First probe failed, retry succeeded — transient blip ignored");
    }
    if (!ok) console.warn(`[NETWORK] Health check failed (${failDetail})`);
    this.state.checking = false;
    if (ok) {
      const wasOffline = !this.state.online;
      this.state.online = true;
      this.state.failures = 0;
      this.state.retryCount = 0;
      const downFor = wasOffline && this._downSince ? Math.round((Date.now() - this._downSince) / 1000) : 0;
      this.state.lastSuccess = Date.now();
      if (wasOffline) {
        this._downSince = null;
        this._logEvent("up", downFor ? `after ${downFor}s down` : "");
        console.info("[NETWORK] Reconnection confirmed");
      }
    } else {
      this.state.failures += 1;
      this._logEvent("fail", `${failDetail} (${reason})`);
      console.warn(`[NETWORK] Consecutive failures: ${this.state.failures}`);
      if (this.state.failures >= FAILURE_THRESHOLD && this.state.online) {
        this.state.online = false;
        this._downSince = Date.now();
        this._logEvent("down", failDetail);
        console.warn("[NETWORK] Backend unreachable — switching to OFFLINE");
      }
    }
    this._emit();
    this._schedule();
  }

  noteApiSuccess() {
    this.state.lastSuccess = Date.now();
    if (this.state.failures > 0) {
      this.state.failures = 0;
      this._emit();
    }
    if (!this.state.online) this.checkNow("api-success-while-offline");
  }

  noteApiFailure() {
    const now = Date.now();
    if (now - this._lastFailureReport < 3000) return;
    this._lastFailureReport = now;
    this.checkNow("api-network-failure");
  }
}

export const connectivity = new ConnectivityManager();
